'use client'

import Link from 'next/link'
import React from 'react'

import { Media } from '@/components/Media'
import { FeaturedPromotionMovingBorder } from '@/components/Promotions/FeaturedPromotionMovingBorder'
import { PromotionBadge, type PromotionBadgeVariant } from '@/components/Promotions/PromotionBadge'
import { PromotionCardOfferDetails } from '@/components/Promotions/PromotionCardOfferDetails'
import { PromotionLocationBadge } from '@/components/Promotions/PromotionLocationBadge'
import type { PromotionOfferDetailsData } from '@/lib/promotions/promotionCard'
import type { PromotionListItem } from '@/lib/promotions/promotionsQuery'
import { DEFAULT_PROMOTION_HERO_IMAGE } from '@/lib/promotions/promotionCard'
import { cn } from '@/utilities/ui'

export type PromotionCardBadge = {
  label: string
  variant: PromotionBadgeVariant
}

export type PromotionCardProps = {
  badges: PromotionCardBadge[]
  body: string
  cta: {
    ariaLabel: string
    href: string
    label: string
  }
  image?: PromotionListItem['heroImage']
  imageAlt: string
  imageSrc?: string
  layout?: 'horizontal' | 'vertical'
  appearance?: 'default' | 'solid'
  listItem?: boolean
  locationLabels?: string[]
  offerDetails?: PromotionOfferDetailsData
  title: string
  /** Featured listing card with layered pink Abyssal Glow border. */
  variant?: 'default' | 'featured'
}

function PromotionCardImage({
  hasCmsImage,
  image,
  imageAlt,
  imageSrc,
  layout,
  variant = 'default',
}: Pick<PromotionCardProps, 'image' | 'imageAlt' | 'imageSrc' | 'layout' | 'variant'> & {
  hasCmsImage: boolean
}) {
  const imageClassName = cn(
    'w-full rounded-[10px] object-cover',
    layout === 'horizontal'
      ? variant === 'featured'
        ? 'h-full min-h-[9rem] lg:min-h-[13rem]'
        : 'h-full min-h-[12rem] lg:min-h-[17.5rem]'
      : 'h-[16.5rem]',
  )

  if (hasCmsImage) {
    return (
      <Media
        className={cn(layout === 'horizontal' ? 'h-full' : 'w-full')}
        imgClassName={imageClassName}
        resource={image}
        size={layout === 'horizontal' ? '50vw' : '33vw'}
      />
    )
  }

  return (
    <img
      alt={imageAlt}
      className={imageClassName}
      src={imageSrc ?? DEFAULT_PROMOTION_HERO_IMAGE}
    />
  )
}

function PromotionCardBadges({
  badges,
  locationLabels,
}: Pick<PromotionCardProps, 'badges' | 'locationLabels'>) {
  if (!badges.length && !locationLabels?.length) {
    return null
  }

  const useSpacedBadges = Boolean(locationLabels?.length)

  if (!useSpacedBadges) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {badges.map((badge) => (
          <PromotionBadge
            key={`${badge.variant}-${badge.label}`}
            label={badge.label}
            variant={badge.variant}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col items-start gap-2 md:w-full md:flex-row md:flex-nowrap md:items-start md:justify-between">
      <div className="flex flex-wrap items-center gap-2">
        {locationLabels!.map((label) => (
          <PromotionLocationBadge key={label} label={label} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 self-start">
        {badges.map((badge) => (
          <PromotionBadge
            key={`${badge.variant}-${badge.label}`}
            label={badge.label}
            variant={badge.variant}
          />
        ))}
      </div>
    </div>
  )
}

function PromotionCardText({
  badges,
  body,
  locationLabels,
  title,
}: Pick<PromotionCardProps, 'badges' | 'body' | 'locationLabels' | 'title'>) {
  return (
    <>
      <PromotionCardBadges badges={badges} locationLabels={locationLabels} />

      <h3 className="font-display text-[1.75rem] font-medium leading-8 tracking-[-0.04em] text-dark-800/80">
        {title}
      </h3>

      {body ? (
        <p className="font-outfit text-base leading-5 text-dark-800/80">{body}</p>
      ) : null}
    </>
  )
}

function PromotionCardCta({
  cta,
  asButton = false,
}: {
  cta: PromotionCardProps['cta']
  asButton?: boolean
}) {
  if (asButton) {
    return (
      <Link
        aria-label={cta.ariaLabel}
        className="mt-auto inline-flex h-12 w-fit items-center justify-center rounded-[10px] border-[1.5px] border-black/30 bg-white/30 px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors hover:bg-white/45 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2"
        href={cta.href}
      >
        {cta.label}
      </Link>
    )
  }

  return (
    <span
      aria-hidden
      className="mt-auto inline-flex h-12 w-fit items-center justify-center rounded-[10px] border-[1.5px] border-black/30 bg-white/30 px-4 font-outfit text-base font-medium text-dark-800/80 transition-colors group-hover:bg-white/45 group-focus-visible:bg-white/45"
    >
      {cta.label}
    </span>
  )
}

function PromotionCardContent({
  badges,
  body,
  cta,
  locationLabels,
  offerDetails,
  showFeaturedOfferDetails,
  title,
}: Pick<
  PromotionCardProps,
  'badges' | 'body' | 'cta' | 'locationLabels' | 'offerDetails' | 'title'
> & {
  showFeaturedOfferDetails: boolean
}) {
  if (showFeaturedOfferDetails && offerDetails) {
    return (
      <div className="flex flex-1 flex-col gap-4 pt-3">
        <Link
          className="group flex flex-col gap-4 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2"
          href={cta.href}
        >
          <PromotionCardText
            badges={badges}
            body={body}
            locationLabels={locationLabels}
            title={title}
          />
        </Link>

        <div className="hidden lg:block">
          <PromotionCardOfferDetails details={offerDetails} />
        </div>

        <PromotionCardCta asButton cta={cta} />
      </div>
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-4 pt-3">
      <PromotionCardText
        badges={badges}
        body={body}
        locationLabels={locationLabels}
        title={title}
      />
      <PromotionCardCta cta={cta} />
    </div>
  )
}

function getPromotionCardShellClassName({
  appearance = 'default',
  isFeatured,
  isHorizontal,
}: {
  appearance?: PromotionCardProps['appearance']
  isFeatured: boolean
  isHorizontal: boolean
}) {
  return cn(
    'flex h-full w-full flex-col rounded-[1.375rem] border-[1.5px] p-3 transition-[box-shadow,background-color,border-color]',
    isHorizontal
      ? cn(
          isFeatured
            ? 'min-h-[15rem] rounded-[calc(1.375rem-4px)] border-transparent bg-white/95 hover:bg-white lg:min-h-[16rem]'
            : 'min-h-[20rem] border-pink-wash bg-white/95 shadow-[0_4px_32px_rgba(174,0,255,0.16)] hover:bg-white',
        )
      : cn(
          'min-h-[33.8125rem] border-black/10',
          appearance === 'solid' ? 'bg-white hover:bg-white' : 'bg-white/70 backdrop-blur-md hover:bg-white/85',
        ),
  )
}

export function PromotionCard({
  appearance = 'default',
  badges,
  body,
  cta,
  image,
  imageAlt,
  imageSrc,
  layout = 'vertical',
  listItem = false,
  locationLabels,
  offerDetails,
  title,
  variant = 'default',
}: PromotionCardProps) {
  const hasCmsImage = Boolean(image && typeof image === 'object')
  const isHorizontal = layout === 'horizontal'
  const isFeatured = variant === 'featured'
  const showFeaturedOfferDetails = isFeatured && Boolean(offerDetails)

  const cardBody = showFeaturedOfferDetails ? (
    <div
      className={cn(
        'flex flex-1 gap-3',
        isHorizontal ? 'flex-col lg:flex-row lg:items-stretch' : 'flex-col',
      )}
    >
      <Link
        aria-hidden
        className={cn(
          'shrink-0 overflow-hidden rounded-[10px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
          isHorizontal ? 'w-full lg:w-[47%]' : 'w-full',
        )}
        href={cta.href}
        tabIndex={-1}
      >
        <PromotionCardImage
          hasCmsImage={hasCmsImage}
          image={image}
          imageAlt={imageAlt}
          imageSrc={imageSrc}
          layout={layout}
          variant={variant}
        />
      </Link>

      <PromotionCardContent
        badges={badges}
        body={body}
        cta={cta}
        locationLabels={locationLabels}
        offerDetails={offerDetails}
        showFeaturedOfferDetails={showFeaturedOfferDetails}
        title={title}
      />
    </div>
  ) : (
    <Link
      aria-label={cta.ariaLabel}
      className={cn(
        'group -m-3 flex flex-1 cursor-pointer gap-3 rounded-[inherit] p-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-turquoise/50 focus-visible:ring-offset-2',
        isHorizontal ? 'flex-col lg:flex-row lg:items-stretch' : 'flex-col',
      )}
      href={cta.href}
    >
      <div
        aria-hidden
        className={cn(
          'shrink-0 overflow-hidden',
          isHorizontal ? 'w-full lg:w-[47%]' : 'w-full',
        )}
      >
        <PromotionCardImage
          hasCmsImage={hasCmsImage}
          image={image}
          imageAlt={imageAlt}
          imageSrc={imageSrc}
          layout={layout}
          variant={variant}
        />
      </div>

      <PromotionCardContent
        badges={badges}
        body={body}
        cta={cta}
        locationLabels={locationLabels}
        offerDetails={offerDetails}
        showFeaturedOfferDetails={false}
        title={title}
      />
    </Link>
  )

  const card = (
    <div
      className={getPromotionCardShellClassName({
        appearance,
        isFeatured,
        isHorizontal,
      })}
    >
      {cardBody}
    </div>
  )

  const content = isFeatured ? (
    <FeaturedPromotionMovingBorder>{card}</FeaturedPromotionMovingBorder>
  ) : (
    card
  )

  if (!listItem) {
    return content
  }

  return <li className="h-full">{content}</li>
}
